import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import {
  AsyncStatus,
  Button,
  Dialog,
  EmptyState,
  Field,
  SectionNav,
  Shell,
  SignOutControl,
  StatusPill,
} from "../components";
import { SupportInbox } from "../components/SupportInbox";
import { AdminDirectoryWorkspace } from "../components/AdminDirectoryWorkspace";
import { AdminOrdersWorkspace } from "../components/AdminOrdersWorkspace";
import { AdminRefundsWorkspace, type AdminRefund } from "../components/AdminRefundsWorkspace";
import { AdminOperationsWorkspace } from "../components/AdminOperationsWorkspace";
import { AdminEntitlementGrantForm } from "../components/AdminEntitlementGrantForm";
import { AdminBenefitsWorkspace } from "../components/AdminBenefitsWorkspace";
import { AdminHealthWorkspace } from "../components/AdminHealthWorkspace";
import { AdminAuditWorkspace } from "../components/AdminAuditWorkspace";
import { adminNavigate, useAdminUrlState } from "../components/admin-navigation";
import { AdminRedemptionCodesPanel } from "../components/RedemptionCodes";
import { setPageMetadata } from "../page-metadata";
import { calculateSiguaMapRefundAmountFen } from "@cheemspay/domain";

interface AdminSummary {
  counts: {
    pendingOrders: number;
    pendingRefunds: number;
    deadTasks: number;
    openDiscrepancies: number;
    waitingSupportTickets: number;
  };
  recentOrders: Array<{
    id: string;
    outTradeNo: string;
    userId: string;
    amountFen: number;
    orderStatus: string;
    fulfillmentStatus: string;
    createdAt: string;
    queryTaskStatus: string | null;
    queryTaskAttempts: number;
    queryTaskLastError: string | null;
    queryTaskUpdatedAt: string | null;
  }>;
  recentNotifications: Array<{
    id: string;
    outTradeNo: string;
    tradeStatus: string | null;
    signatureValid: boolean;
    processingStatus: string;
    failureReason: string | null;
    receivedAt: string;
  }>;
  recentEmailEvents: Array<{
    id: string;
    kind: string;
    status: string;
    createdAt: string;
  }>;
  recentRefunds: Array<{
    id: string;
    orderId: string;
    outTradeNo: string;
    userId: string;
    requestedAmountFen: number;
    amountFen: number;
    reasonCode: string;
    reason: string;
    refundPolicyCode: string;
    status: string;
    providerRefundNo: string | null;
    createdAt: string;
    updatedAt: string;
    taskStatus: string | null;
    taskAttempts: number;
    taskLastError: string | null;
    taskUpdatedAt: string | null;
    taskAuthorized: boolean;
  }>;
}

interface AdminProduct {
  productId: string;
  appId: string;
  code: string;
  name: string;
  description: string;
  purchaseLimit: number | null;
  sortOrder: number;
  active: boolean;
  priceVersionId: string;
  amountFen: number;
  originalAmountFen: number | null;
  durationSeconds: number;
  currency: string;
  features: string[];
  maxDevices: number;
  updatedAt: string;
}

const emptyProduct: Omit<AdminProduct, "productId" | "priceVersionId" | "updatedAt"> = {
  appId: "bomana",
  code: "",
  name: "",
  description: "",
  purchaseLimit: null,
  sortOrder: 30,
  active: true,
  amountFen: 100,
  originalAmountFen: null,
  durationSeconds: 3 * 86_400,
  currency: "CNY",
  features: ["bomana.super_bomber"],
  maxDevices: 2,
};

type AdminWorkspace =
  | "overview"
  | "support"
  | "refunds"
  | "orders"
  | "operations"
  | "products"
  | "redemptions"
  | "grants"
  | "users"
  | "teams"
  | "integrations"
  | "benefits" | "health" | "audit";

function refundPolicyReviewText(refund: AdminRefund): string {
  if (refund.reasonCode !== "CHANGED_MIND") {
    return "责任或异常退款：按实际证据人工复核";
  }
  if (refund.refundPolicyCode === "LOW_PRICE_14_DAY_HALF") {
    return "低价政策：14 天内退实付金额的 50%";
  }
  if (refund.refundPolicyCode === "HIGH_PRICE_SIGUAMAP_168_HOUR_USAGE") {
    return "高价政策：168 小时内，0/1/2/3/4/5+ 次按 100/85/70/55/40/0%";
  }
  return "历史政策：按剩余服务时间折算并扣 5%";
}

function highPriceRefundAmountFen(amountFen: number, usageCount: number): number {
  return calculateSiguaMapRefundAmountFen(amountFen, usageCount);
}

const ADMIN_WORKSPACES: Array<{
  id: AdminWorkspace;
  label: string;
  description: string;
}> = [
  { id: "overview", label: "待办总览", description: "优先处理需要人工介入的事项" },
  { id: "support", label: "客服工单", description: "回复用户与跟进问题" },
  { id: "refunds", label: "退款审核", description: "复核退款并决定处理方式" },
  { id: "orders", label: "订单与查单", description: "查看履约状态与发起查单" },
  { id: "operations", label: "异常处理", description: "跟进失败任务与对账差异" },
  { id: "health", label: "运行与对账", description: "检查后台任务、备份、恢复和每日账单" },
  { id: "audit", label: "操作记录", description: "搜索用户与管理员操作记录" },
  { id: "products", label: "商品管理", description: "维护商品、价格与授权规则" },
  { id: "benefits", label: "全局权益", description: "统一调整所有 Bomana 订阅的功能与设备规则" },
  { id: "redemptions", label: "兑换码", description: "配置兑换内容、有效期和次数" },
  { id: "grants", label: "人工授权", description: "查询账号并增加订阅" },
  { id: "users", label: "用户", description: "只读查看账户、权益和设备摘要" },
  { id: "teams", label: "战队", description: "查看商业战队并受控增加体验授权" },
  { id: "integrations", label: "集成状态", description: "检查回调与邮件事件" },
];

const PRODUCT_DEPENDENT_WORKSPACES = new Set<AdminWorkspace>([
  "products",
  "redemptions",
  "grants",
]);

function isAdminAuthenticationError(caught: unknown): boolean {
  return (
    caught instanceof ApiRequestError &&
    (caught.status === 401 || caught.code === "ADMIN_2FA_REQUIRED")
  );
}

function adminErrorMessage(caught: unknown, fallback: string): string {
  if (
    caught instanceof ApiRequestError &&
    caught.code === "ADMIN_2FA_REQUIRED"
  ) {
    return "当前管理员会话未满足双因素认证要求。请重新登录，并确认验证器已开启。";
  }
  if (caught instanceof ApiRequestError && caught.status === 401) {
    return "管理员会话已失效，请重新登录后继续。";
  }
  return caught instanceof Error ? caught.message : fallback;
}

export function AdminPage() {
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [workspaceValue, setWorkspace] = useAdminUrlState<AdminWorkspace>("workspace", "overview", "push");
  const workspace = ADMIN_WORKSPACES.some((item) => item.id === workspaceValue) ? workspaceValue : "overview";
  const [orderTarget, setOrderTarget] = useAdminUrlState("order", "");
  const [refundQuery, setRefundQuery] = useAdminUrlState("refundQuery", "");
  const [error, setError] = useState("");
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [notice, setNotice] = useState("");
  const [busyRefund, setBusyRefund] = useState("");
  const [refundReview, setRefundReview] = useState<AdminRefund | null>(null);
  const [refundReviewAmount, setRefundReviewAmount] = useState("");
  const [refundReviewUsageCount, setRefundReviewUsageCount] = useState("");
  const [refundReviewReason, setRefundReviewReason] = useState("");
  const [refundReviewError, setRefundReviewError] = useState("");
  const [editingProductId, setEditingProductId] = useState("");
  const [creatingProduct, setCreatingProduct] = useState(false);
  const summaryRequestVersion = useRef(0);

  const refreshSummary = async () => {
    const requestVersion = ++summaryRequestVersion.current;
    setRefreshing(true);
    setError("");
    setAuthenticationRequired(false);
    try {
      const response = await apiRequest<{ summary: AdminSummary }>(
        "/api/admin/summary",
      );
      if (requestVersion === summaryRequestVersion.current) {
        setSummary(response.summary);
        setLastRefreshedAt(new Date());
      }
    } catch (caught) {
      if (requestVersion === summaryRequestVersion.current) {
        setAuthenticationRequired(isAdminAuthenticationError(caught));
        setError(adminErrorMessage(caught, "无法刷新后台客服摘要。"));
      }
    } finally {
      if (requestVersion === summaryRequestVersion.current) {
        setRefreshing(false);
      }
    }
  };

  const load = async () => {
    const requestVersion = ++summaryRequestVersion.current;
    setError("");
    setAuthenticationRequired(false);
    setRefreshing(true);
    try {
      const [summaryResult, productResult] = await Promise.allSettled([
        apiRequest<{ summary: AdminSummary }>("/api/admin/summary"),
        apiRequest<{ products: AdminProduct[] }>(
          "/api/admin/products?appId=bomana",
        ),
      ]);
      if (requestVersion !== summaryRequestVersion.current) {
        return;
      }
      const failures = [summaryResult, productResult].filter(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );
      if (summaryResult.status === "fulfilled") {
        setSummary(summaryResult.value.summary);
      }
      if (productResult.status === "fulfilled") {
        setProducts(productResult.value.products);
        setProductsLoaded(true);
      }
      if (summaryResult.status === "fulfilled" || productResult.status === "fulfilled") {
        setLastRefreshedAt(new Date());
      }
      if (failures.length) {
        const firstFailure = failures[0]?.reason;
        setAuthenticationRequired(
          failures.some((failure) => isAdminAuthenticationError(failure.reason)),
        );
        setError(
          failures.length === 2
            ? adminErrorMessage(firstFailure, "无法读取后台；管理员必须启用并完成 2FA。")
            : `${adminErrorMessage(firstFailure, "部分后台数据无法刷新。")} 其余数据仍已更新。`,
        );
      }
    } finally {
      if (requestVersion === summaryRequestVersion.current) {
        setLoaded(true);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    setPageMetadata(
      "管理后台 | CheemsPay",
      "CheemsPay 管理后台：处理客服工单、退款审核、订单查单、商品、兑换码与人工授权。",
    );
  }, []);

  const authorizeRefund = async (
    refundId: string,
    amountYuan: string,
    reason: string,
    qualifyingUsageCount?: number,
  ): Promise<boolean> => {
    const amountFen = parseYuan(amountYuan);
    if (amountFen === null) {
      setRefundReviewError("请填写有效的授权退款金额（元）");
      return false;
    }
    if (!reason.trim()) {
      setRefundReviewError("请填写授权退款的后台原因");
      return false;
    }
    setBusyRefund(refundId);
    setError("");
    setRefundReviewError("");
    try {
      await apiRequest("/api/admin/refunds/" + refundId + "/authorize", {
        method: "POST",
        body: JSON.stringify({
          amountFen,
          qualifyingUsageCount,
          reason: reason.trim(),
        }),
      });
      await load();
      setNotice("退款已授权并提交支付宝处理，请在任务状态中跟进结果。");
      return true;
    } catch (caught) {
      setRefundReviewError(caught instanceof Error ? caught.message : "无法授权退款");
      return false;
    } finally {
      setBusyRefund("");
    }
  };

  const closeRefundWithoutRefund = async (
    refundId: string,
    reason: string,
  ): Promise<boolean> => {
    if (!reason.trim()) {
      setRefundReviewError("请填写关闭退款单的后台原因");
      return false;
    }
    setBusyRefund(refundId);
    setError("");
    setNotice("");
    setRefundReviewError("");
    try {
      await apiRequest(`/api/admin/refunds/${refundId}/close`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      await load();
      setNotice("退款单已关闭，未调用支付宝退款接口。");
      return true;
    } catch (caught) {
      setNotice("");
      setRefundReviewError(
        caught instanceof Error ? caught.message : "无法关闭退款单",
      );
      return false;
    } finally {
      setBusyRefund("");
    }
  };

  const productSaved = async () => {
    setCreatingProduct(false);
    setEditingProductId("");
    await load();
  };

  const workspaceItems = ADMIN_WORKSPACES.map(({ id, label }) => ({
    id,
    label,
    badge:
      id === "support"
        ? summary?.counts.waitingSupportTickets
        : id === "refunds"
          ? summary?.counts.pendingRefunds
          : id === "operations"
            ? summary
              ? summary.counts.deadTasks + summary.counts.openDiscrepancies
              : null
            : null,
  }));
  const workspaceDataUnavailable =
    (["overview", "support", "integrations"] as AdminWorkspace[]).includes(
      workspace,
    )
      ? !summary
      : PRODUCT_DEPENDENT_WORKSPACES.has(workspace)
        ? !productsLoaded
        : false;

  return (
    <Shell
      action={<SignOutControl />}
      back={{ href: "/account", label: "返回账户" }}
    >
      <section className="admin-page">
        <h1>管理后台</h1>
        <p>
          处理客服、退款和订单异常。商品、授权与集成状态在各自工作区。
        </p>

        <div className="admin-workspace-toolbar">
          <div className="admin-refresh-status" aria-live="polite">
            <span>
              {lastRefreshedAt
                ? `最近刷新：${lastRefreshedAt.toLocaleString("zh-CN")}`
                : "尚未取得后台数据"}
            </span>
            <Button
              className="secondary-button"
              busy={refreshing}
              busyLabel="刷新中…"
              onClick={() => void load()}
              type="button"
            >
              刷新数据
            </Button>
          </div>
          <SectionNav
            activeItem={workspace}
            items={workspaceItems}
            label="管理工作区"
            onSelect={setWorkspace}
          />
        </div>

        {error ? (
          <AsyncStatus
            action={
              authenticationRequired ? (
                <a className="button button-secondary" href="/?returnTo=%2Fadmin">
                  重新登录管理员账户
                </a>
              ) : undefined
            }
            tone="error"
          >
            <strong>刷新未完成：</strong>{error}
            {summary || productsLoaded
              ? " 以下仍显示最近一次成功读取的数据。"
              : ""}
          </AsyncStatus>
        ) : null}
        {notice ? <div className="form-message">{notice}</div> : null}
        {!loaded ? <div className="page-loading">正在读取后台状态…</div> : null}

        {loaded ? (
          <>
            {workspace === "benefits" ? <AdminBenefitsWorkspace /> : null}
            {workspace === "health" ? <AdminHealthWorkspace /> : null}
            {workspace === "audit" ? <AdminAuditWorkspace /> : null}
            {workspace === "products" && productsLoaded ? (
            <article className="dashboard-card product-console">
              <div className="card-title-row product-console-heading">
                <div>
                  <span className="card-kicker">商品管理</span>
                  <h2>在售商品与授权规则</h2>
                  <p>
                    修改价格会新建价格版本；已创建的订单仍保留原下单金额。停用商品不会删除历史订单。
                  </p>
                </div>
                <Button
                  disabled={creatingProduct}
                  onClick={() => {
                    setEditingProductId("");
                    setCreatingProduct(true);
                  }}
                  type="button"
                >
                  新增商品
                </Button>
              </div>

              {creatingProduct ? (
                <ProductEditor
                  onCancel={() => setCreatingProduct(false)}
                  onSaved={productSaved}
                />
              ) : null}

              <div className="admin-product-list">
                {products.map((product) =>
                  editingProductId === product.productId ? (
                    <ProductEditor
                      key={product.productId}
                      onCancel={() => setEditingProductId("")}
                      onSaved={productSaved}
                      product={product}
                    />
                  ) : (
                    <div className="admin-product-row" key={product.productId}>
                      <div className="admin-product-main">
                        <div>
                          <StatusPill tone={product.active ? "good" : "neutral"}>
                            {product.active ? "在售" : "已下架"}
                          </StatusPill>
                          <code>{product.code}</code>
                        </div>
                        <h3>{product.name}</h3>
                        <p>{product.description}</p>
                      </div>
                      <div className="admin-product-price">
                        {product.originalAmountFen !== null ? (
                          <del>
                            ¥{(product.originalAmountFen / 100).toFixed(2)}
                          </del>
                        ) : null}
                        <strong>¥{(product.amountFen / 100).toFixed(2)}</strong>
                      </div>
                      <div className="admin-product-rules">
                        <span>
                          {Math.round(product.durationSeconds / 86_400)} 天
                        </span>
                        <span>{product.maxDevices} 台设备</span>
                        <span>
                          {product.purchaseLimit === null
                            ? "不限购"
                            : `每账号限购 ${product.purchaseLimit} 次`}
                        </span>
                      </div>
                      <button
                        className="secondary-button"
                        onClick={() => {
                          setCreatingProduct(false);
                          setEditingProductId(product.productId);
                        }}
                        type="button"
                      >
                        编辑
                      </button>
                    </div>
                  ),
                )}
              </div>
            </article>
            ) : null}

            {workspace === "grants" && productsLoaded ? <AdminEntitlementGrantForm
              products={products}
              onGranted={(result) => {
                setNotice(
                  result.idempotent
                    ? `人工订阅请求已幂等确认：${result.username} 的 Bomana 订阅记录（${result.productName}）到期日为 ${new Date(result.resultingExpiry).toLocaleString()}`
                    : `已为 ${result.username} 的所选 Bomana 授权记录增加 ${result.durationDays} 天，关联商品 ${result.productName}，到期日为 ${new Date(result.resultingExpiry).toLocaleString()}`,
                );
              }}
            /> : null}

            {workspace === "redemptions" && productsLoaded ? (
              <AdminRedemptionCodesPanel products={products} />
            ) : null}

            {workspace === "users" ? <AdminDirectoryWorkspace kind="users" /> : null}

            {workspace === "orders" ? <AdminOrdersWorkspace initialOrderId={orderTarget} refreshVersion={lastRefreshedAt?.getTime() ?? 0}
              onRefunds={(query) => adminNavigate("refunds", { "refunds.filters": { query, status: "", page: 1 } })} /> : null}

            {workspace === "refunds" ? <AdminRefundsWorkspace initialQuery={refundQuery} refreshVersion={lastRefreshedAt?.getTime() ?? 0}
              onOrder={(id) => adminNavigate("orders", { order: id })}
              onReview={(refund) => {
                setRefundReview(refund); setRefundReviewAmount((refund.amountFen / 100).toFixed(2));
                setRefundReviewUsageCount(""); setRefundReviewReason(""); setRefundReviewError("");
              }} /> : null}

            {workspace === "operations" ? <AdminOperationsWorkspace refreshVersion={lastRefreshedAt?.getTime() ?? 0}
              onChange={() => void refreshSummary()}
              onOrder={(id) => adminNavigate("orders", { order: id })}
              onRefund={(query) => adminNavigate("refunds", { "refunds.filters": { query, status: "", page: 1 } })} /> : null}

            {workspace === "teams" ? <AdminDirectoryWorkspace kind="teams" /> : null}

            {workspace === "support" && summary ? (
              <SupportInbox
                onChanged={refreshSummary}
                waitingCount={summary.counts.waitingSupportTickets}
              />
            ) : null}

            {summary ? (
              <>
                {workspace === "overview" ? (
                <>
                <div className="admin-section-heading">
                  <span className="card-kicker">待办</span>
                  <h2>需要处理的事项</h2>
                </div>
                <div className="admin-metrics">
                  <AdminMetric
                    label="待支付订单"
                    value={summary.counts.pendingOrders}
                  />
                  <AdminMetric
                    label="待处理退款"
                    value={summary.counts.pendingRefunds}
                  />
                  <AdminMetric
                    label="失败任务"
                    value={summary.counts.deadTasks}
                  />
                  <AdminMetric
                    label="对账差异"
                    value={summary.counts.openDiscrepancies}
                  />
                  <AdminMetric
                    label="待回复工单"
                    value={summary.counts.waitingSupportTickets}
                  />
                </div>
                <div className="admin-priority-queue">
                  <button
                    className="admin-priority-item"
                    onClick={() => setWorkspace("support")}
                    type="button"
                  >
                    <strong>{summary.counts.waitingSupportTickets} 条待回复工单</strong>
                    <span>进入客服队列</span>
                  </button>
                  <button
                    className="admin-priority-item"
                    onClick={() => setWorkspace("refunds")}
                    type="button"
                  >
                    <strong>{summary.counts.pendingRefunds} 条待审核退款</strong>
                    <span>打开退款审核</span>
                  </button>
                  <button
                    className="admin-priority-item"
                    onClick={() => setWorkspace("operations")}
                    type="button"
                  >
                    <strong>{summary.counts.deadTasks + summary.counts.openDiscrepancies} 项任务与对账异常</strong>
                    <span>查看失败原因与处理记录</span>
                  </button>
                </div>
                </>
                ) : null}

                {workspace === "integrations" ? (
                <div className="admin-two-column">
                  <article className="dashboard-card">
                    <span className="card-kicker">支付宝通知</span>
                    <h2>最近回调</h2>
                    <div className="compact-list">
                      {summary.recentNotifications.map((notification) => (
                        <div key={notification.id}>
                          <span>
                            <strong>{notification.outTradeNo}</strong>
                            <small>
                              {formatAdminStatus(notification.processingStatus)}
                              {notification.failureReason
                                ? ` · ${notification.failureReason}`
                                : ""}
                            </small>
                          </span>
                          <StatusPill
                            tone={notification.signatureValid ? "good" : "warn"}
                          >
                            {notification.signatureValid
                              ? "验签通过"
                              : "验签拒绝"}
                          </StatusPill>
                        </div>
                      ))}
                    </div>
                  </article>

                  <article className="dashboard-card">
                    <span className="card-kicker">腾讯云 SES</span>
                    <h2>邮件事件</h2>
                    <div className="compact-list">
                      {summary.recentEmailEvents.map((event) => (
                        <div key={event.id}>
                          <span>
                            <strong>{formatAdminStatus(event.kind)}</strong>
                            <small>
                              {new Date(event.createdAt).toLocaleString()}
                            </small>
                          </span>
                          <StatusPill
                            tone={event.status === "SENT" ? "good" : "warn"}
                          >
                            {formatAdminStatus(event.status)}
                          </StatusPill>
                        </div>
                      ))}
                    </div>
                  </article>
                </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
        {refundReview ? (
          <RefundReviewDialog
            amountYuan={refundReviewAmount}
            busy={busyRefund === refundReview.id}
            error={refundReviewError}
            onAmountChange={setRefundReviewAmount}
            onUsageCountChange={(value) => {
              setRefundReviewUsageCount(value);
              if (value !== "") {
                const amountFen = highPriceRefundAmountFen(
                  refundReview.orderAmountFen,
                  Number(value),
                );
                setRefundReviewAmount((amountFen / 100).toFixed(2));
                setRefundReviewReason(
                  `已按高价退款政策复核：有效使用 ${Number(value) >= 5 ? "5 次及以上" : `${value} 次`}`,
                );
              }
              setRefundReviewError("");
            }}
            onAuthorize={async () => {
              const needsUsageCount =
                !refundReview.taskAuthorized &&
                refundReview.reasonCode === "CHANGED_MIND" &&
                refundReview.refundPolicyCode ===
                  "HIGH_PRICE_SIGUAMAP_168_HOUR_USAGE";
              if (needsUsageCount && refundReviewUsageCount === "") {
                setRefundReviewError("请先填写有效使用次数");
                return;
              }
              if (
                await authorizeRefund(
                  refundReview.id,
                  refundReviewAmount,
                  refundReviewReason,
                  needsUsageCount ? Number(refundReviewUsageCount) : undefined,
                )
              ) {
                setRefundReview(null);
              }
            }}
            onClose={() => {
              if (!busyRefund) {
                setRefundReview(null);
              }
            }}
            onCloseWithoutRefund={async () => {
              if (
                await closeRefundWithoutRefund(
                  refundReview.id,
                  refundReviewReason,
                )
              ) {
                setRefundReview(null);
              }
            }}
            onReasonChange={setRefundReviewReason}
            reason={refundReviewReason}
            refund={refundReview}
            usageCount={refundReviewUsageCount}
          />
        ) : null}
        {workspaceDataUnavailable && loaded ? (
          <EmptyState
            title={
              PRODUCT_DEPENDENT_WORKSPACES.has(workspace)
                ? "商品数据暂不可用"
                : "后台摘要暂不可用"
            }
          >
            {PRODUCT_DEPENDENT_WORKSPACES.has(workspace)
              ? "请使用“刷新数据”重新读取商品与授权规则；其他已加载的工作区仍可继续使用。"
              : "请使用“刷新数据”重新读取后台摘要；商品与人工授权工作区中的已加载数据仍可继续使用。"}
          </EmptyState>
        ) : null}
      </section>
    </Shell>
  );
}

function RefundReviewDialog({
  refund,
  amountYuan,
  reason,
  usageCount,
  busy,
  error,
  onAmountChange,
  onUsageCountChange,
  onReasonChange,
  onAuthorize,
  onCloseWithoutRefund,
  onClose,
}: {
  refund: AdminRefund;
  amountYuan: string;
  reason: string;
  usageCount: string;
  busy: boolean;
  error: string;
  onAmountChange: (value: string) => void;
  onUsageCountChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onAuthorize: () => Promise<void>;
  onCloseWithoutRefund: () => Promise<void>;
  onClose: () => void;
}) {
  const needsUsageCount =
    !refund.taskAuthorized &&
    refund.reasonCode === "CHANGED_MIND" &&
    refund.refundPolicyCode === "HIGH_PRICE_SIGUAMAP_168_HOUR_USAGE";
  const noRefund = needsUsageCount && Number(usageCount) >= 5;
  return (
    <Dialog
      className="admin-refund-review-dialog"
      closeLabel="关闭退款复核"
      description="请在两种处理方式中选择一种：授权会提交支付宝退款；关闭不会调用支付宝。"
      dismissible={!busy}
      footer={
        <div className="admin-refund-review-actions">
          <Button
            busy={busy}
            busyLabel="处理中…"
            onClick={() => void onCloseWithoutRefund()}
            disabled={refund.taskAuthorized}
            type="button"
            variant="danger"
          >
            关闭申请（不退款）
          </Button>
          <Button
            busy={busy}
            busyLabel="处理中…"
            disabled={noRefund}
            onClick={() => void onAuthorize()}
            type="button"
          >
            授权并提交支付宝退款
          </Button>
        </div>
      }
      onClose={onClose}
      open
      title="决定如何处理这笔退款"
    >
      <span className="card-kicker">退款复核</span>
      <div className="admin-refund-review-context">
        <div>
          <span>订单号</span>
          <strong>{refund.outTradeNo}</strong>
        </div>
        <div>
          <span>申请账号</span>
          <strong>{refund.username ?? refund.userId}</strong>
        </div>
        <div>
          <span>订单实付</span>
          <strong>¥{(refund.orderAmountFen / 100).toFixed(2)}</strong>
        </div>
        <div>
          <span>建议退款</span>
          <strong>¥{(refund.requestedAmountFen / 100).toFixed(2)}</strong>
        </div>
        <div>
          <span>申请时间</span>
          <strong>{new Date(refund.createdAt).toLocaleString("zh-CN")}</strong>
        </div>
        <p>{refund.reason}</p>
        <p>{refundPolicyReviewText(refund)}</p>
      </div>

      <div className="admin-refund-review-fields">
        {refund.taskAuthorized ? <p className="admin-card-note">此退款已提交过授权。再次处理会沿用原退款单和原授权金额；请先核对渠道退款结果。</p> : null}
        {needsUsageCount ? (
          <Field
            label="有效使用次数"
            hint="5 次及以上应关闭申请，不调用支付宝退款"
          >
            <select
              disabled={busy}
              onChange={(event) => onUsageCountChange(event.target.value)}
              value={usageCount}
            >
              <option value="">请选择</option>
              <option value="0">0 次 · 退 100%</option>
              <option value="1">1 次 · 退 85%</option>
              <option value="2">2 次 · 退 70%</option>
              <option value="3">3 次 · 退 55%</option>
              <option value="4">4 次 · 退 40%</option>
              <option value="5">5 次及以上 · 不退款</option>
            </select>
          </Field>
        ) : null}
        <Field label="本次授权金额（元）" hint="仅在授权退款时提交给支付宝">
          <input
            disabled={busy || needsUsageCount || refund.taskAuthorized || (refund.reasonCode === "CHANGED_MIND" && refund.refundPolicyCode === "LOW_PRICE_14_DAY_HALF")}
            inputMode="decimal"
            onChange={(event) => onAmountChange(event.target.value)}
            value={amountYuan}
          />
        </Field>
        <label className="field">
          <span className="field-label">后台处理原因</span>
          <textarea
            disabled={busy}
            maxLength={500}
            onChange={(event) => onReasonChange(event.target.value)}
            rows={4}
            value={reason}
          />
        </label>
      </div>

      {error ? <AsyncStatus tone="error">{error}</AsyncStatus> : null}
    </Dialog>
  );
}

function ProductEditor({
  product,
  onSaved,
  onCancel,
}: {
  product?: AdminProduct;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const source = product ?? emptyProduct;
  const [appId, setAppId] = useState(source.appId);
  const [code, setCode] = useState(source.code);
  const [name, setName] = useState(source.name);
  const [description, setDescription] = useState(source.description);
  const [amountYuan, setAmountYuan] = useState(
    (source.amountFen / 100).toFixed(2),
  );
  const [originalAmountYuan, setOriginalAmountYuan] = useState(
    source.originalAmountFen === null
      ? ""
      : (source.originalAmountFen / 100).toFixed(2),
  );
  const [durationDays, setDurationDays] = useState(
    String(Math.round(source.durationSeconds / 86_400)),
  );
  const [features, setFeatures] = useState(source.features.join(", "));
  const [maxDevices, setMaxDevices] = useState(String(source.maxDevices));
  const [purchaseLimit, setPurchaseLimit] = useState(
    source.purchaseLimit === null ? "" : String(source.purchaseLimit),
  );
  const [sortOrder, setSortOrder] = useState(String(source.sortOrder));
  const [active, setActive] = useState(source.active);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [reviewBody, setReviewBody] = useState<Record<string, unknown> | null>(null);

  const saveReviewed = async () => {
    if (!reviewBody) return;
    setBusy(true); setError("");
    try {
      await apiRequest(product ? `/api/admin/products/${product.productId}` : "/api/admin/products", {
        method: product ? "PUT" : "POST", body: JSON.stringify(reviewBody),
      });
      setReviewBody(null); await onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "商品保存失败"); }
    finally { setBusy(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const amountFen = parseYuan(amountYuan);
      const originalAmountFen =
        originalAmountYuan.trim() === ""
          ? null
          : parseYuan(originalAmountYuan);
      const days = Number(durationDays);
      const devices = Number(maxDevices);
      const limit =
        purchaseLimit.trim() === "" ? null : Number(purchaseLimit);
      const order = Number(sortOrder);
      if (
        amountFen === null ||
        (originalAmountYuan.trim() !== "" && originalAmountFen === null) ||
        !Number.isInteger(days) ||
        days < 1 ||
        !Number.isInteger(devices) ||
        devices < 1 ||
        (limit !== null && (!Number.isInteger(limit) || limit < 1)) ||
        !Number.isInteger(order)
      ) {
        throw new Error("请检查金额、授权天数、设备数、限购次数和排序值");
      }
      if (changeReason.trim().length < 3) throw new Error("请填写至少 3 字的修改原因");
      setReviewBody({
            reason: changeReason.trim(),
            expectedUpdatedAt: product?.updatedAt,
            appId,
            code,
            name,
            description,
            amountFen,
            originalAmountFen,
            durationSeconds: days * 86_400,
            currency: "CNY",
            features: features
              .split(/[\n,]/)
              .map((feature) => feature.trim())
              .filter(Boolean),
            maxDevices: devices,
            purchaseLimit: limit,
            sortOrder: order,
            active,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "商品保存失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="product-editor" onSubmit={submit}>
      <div className="product-editor-title">
        <div>
          <span className="card-kicker">
            {product ? "编辑商品" : "新商品"}
          </span>
          <h3>{product ? product.name : "创建一项可售授权"}</h3>
        </div>
        <label className="admin-switch">
          <input
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            type="checkbox"
          />
          <span>{active ? "上架" : "下架"}</span>
        </label>
      </div>

      <div className="product-editor-grid">
        <Field label="应用 ID" hint="创建后固定；例如 bomana">
          <input
            readOnly={Boolean(product)}
            onChange={(event) => setAppId(event.target.value)}
            required
            value={appId}
          />
        </Field>
        <Field label="商品代码" hint="创建后固定，更换代码请新建商品">
          <input
            readOnly={Boolean(product)}
            onChange={(event) => setCode(event.target.value)}
            placeholder="super-bomber-..."
            required
            value={code}
          />
        </Field>
        <Field label="商品名称">
          <input
            onChange={(event) => setName(event.target.value)}
            required
            value={name}
          />
        </Field>
        <Field label="功能标识" hint={appId === "bomana" ? "统一由全局权益管理，购买商品只增加订阅时长" : "多个功能用逗号分隔"}>
          <input
            readOnly={appId === "bomana"}
            onChange={(event) => setFeatures(event.target.value)}
            required
            value={features}
          />
        </Field>
        <Field label="现价（元）">
          <input
            inputMode="decimal"
            onChange={(event) => setAmountYuan(event.target.value)}
            required
            value={amountYuan}
          />
        </Field>
        <Field label="原价（元，可选）" hint="填写后前台显示划线价">
          <input
            inputMode="decimal"
            onChange={(event) => setOriginalAmountYuan(event.target.value)}
            placeholder="留空则不显示"
            value={originalAmountYuan}
          />
        </Field>
        <Field label="授权天数">
          <input
            inputMode="numeric"
            min="1"
            onChange={(event) => setDurationDays(event.target.value)}
            required
            type="number"
            value={durationDays}
          />
        </Field>
        <Field label="设备上限">
          <input
            readOnly={appId === "bomana"}
            inputMode="numeric"
            min="1"
            onChange={(event) => setMaxDevices(event.target.value)}
            required
            type="number"
            value={maxDevices}
          />
        </Field>
        <Field label="每账号限购次数" hint="留空表示不限购">
          <input
            inputMode="numeric"
            min="1"
            onChange={(event) => setPurchaseLimit(event.target.value)}
            placeholder="不限购"
            type="number"
            value={purchaseLimit}
          />
        </Field>
        <Field label="展示顺序" hint="数字越小越靠前">
          <input
            inputMode="numeric"
            onChange={(event) => setSortOrder(event.target.value)}
            required
            type="number"
            value={sortOrder}
          />
        </Field>
        <label className="field product-description-field">
          <span className="field-label">商品说明</span>
          <textarea
            maxLength={1_000}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            value={description}
          />
        </label>
      </div>

      <Field label="商品修改原因"><input required minLength={3} maxLength={500} value={changeReason} onChange={(event) => setChangeReason(event.target.value)} /></Field>
      {error ? <div className="form-error" role="alert">{error}</div> : null}
      <div className="product-editor-actions">
        <button
          className="secondary-button"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          取消
        </button>
        <Button disabled={busy} type="submit">
          {busy ? "保存中…" : "预览商品修改"}
        </Button>
      </div>
      {reviewBody ? <Dialog open title="确认商品修改" dismissible={!busy} onClose={() => { if (!busy) setReviewBody(null); }}>
        <p>商品修改只影响以后创建的订单。已有订单的价格、退款政策和订阅到期日保留。</p>
        <dl className="admin-directory-facts">
          <div><dt>商品名称</dt><dd>{source.name || "新商品"} → {String(reviewBody.name)}</dd></div>
          <div><dt>实付价格</dt><dd>¥{(source.amountFen / 100).toFixed(2)} → ¥{(Number(reviewBody.amountFen) / 100).toFixed(2)}</dd></div>
          <div><dt>订阅时长</dt><dd>{source.durationSeconds / 86400} 天 → {Number(reviewBody.durationSeconds) / 86400} 天</dd></div>
          <div><dt>上架状态</dt><dd>{source.active ? "上架" : "下架"} → {reviewBody.active ? "上架" : "下架"}</dd></div>
          <div><dt>限购次数</dt><dd>{source.purchaseLimit ?? "不限"} → {String(reviewBody.purchaseLimit ?? "不限")}</dd></div>
          <div><dt>权益</dt><dd>{appId === "bomana" ? "所有订阅统一使用 Bomana 全局权益" : `${source.features.join(", ")} → ${String(reviewBody.features)}`}</dd></div>
          <div><dt>原因</dt><dd>{String(reviewBody.reason)}</dd></div>
        </dl>
        {error ? <p role="alert" className="form-error">{error}</p> : null}
        <Button type="button" busy={busy} onClick={() => void saveReviewed()}>确认保存商品</Button>
      </Dialog> : null}
    </form>
  );
}

function parseYuan(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
    return null;
  }
  const [yuan = "0", fraction = ""] = normalized.split(".");
  const fen = Number(yuan) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(fen) && fen > 0 ? fen : null;
}

function AdminMetric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function formatAdminStatus(value: string): string {
  const labels: Record<string, string> = {
    PENDING: "待处理",
    RUNNING: "处理中",
    SUCCEEDED: "已完成",
    FAILED: "失败",
    DEAD: "已终止",
    CLOSED: "已关闭",
    FULFILLED: "已履约",
    PAID: "已支付",
    CREATED: "已创建",
    CANCELLED: "已取消",
    REJECTED: "已拒绝",
    SENT: "已发送",
    DELIVERED: "已送达",
    BOUNCED: "投递失败",
    COMPLAINED: "投诉",
    OPENED: "已打开",
    CLICKED: "已点击",
    MANUAL: "人工发放",
    ACTIVE: "生效中",
    EXPIRED: "已失效",
  };
  return labels[value] ?? value;
}
